/**
 * Thin fetch wrappers around `/v1/analytics/*`. Reuses the shared `request`
 * helper conventions (Firebase ID-token injection, TransformInterceptor
 * unwrapping, 401 → refresh → redirect) by going through `lib/api.ts`.
 *
 * The page calls these via the `useAnalytics` hooks (./hooks.ts) so React
 * never sees fetch shapes directly.
 */

import { API_BASE } from '@/src/infrastructure/lib/api'
import { auth } from '@/src/infrastructure/config/firebase'
import type { AnalyticsRange, ActivityRow, DashboardPayload, RevenuePayload } from './types'

async function authFetch<T>(url: string): Promise<T> {
  const token = await auth.currentUser?.getIdToken().catch(() => null)
  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
  if (!res.ok) {
    const body = await res.json().catch(() => ({}))
    throw new Error(body?.message ?? `HTTP ${res.status}`)
  }
  const body = await res.json()
  return (body?.data !== undefined ? body.data : body) as T
}

export function fetchDashboard(range: AnalyticsRange) {
  return authFetch<DashboardPayload>(`${API_BASE}/analytics/dashboard?range=${range}`)
}

export function fetchRevenue(range: AnalyticsRange) {
  return authFetch<RevenuePayload>(`${API_BASE}/analytics/revenue?range=${range}`)
}

export function fetchRecentActivities(limit = 10) {
  return authFetch<ActivityRow[]>(`${API_BASE}/analytics/recent-activities?limit=${limit}`)
}
